/**
 * OWA-1463: the firm's calendar day.
 *
 * Invoice dates (issue, start and end dates) are calendar days. Transactions are instants. To
 * decide whether a transaction falls on or before an invoice's day, the day has to become an
 * instant, and that needs a zone. Using the viewer's browser zone made the answer depend on where
 * the viewer's computer is: a payment from trust saved at 11:59:59 pm Central falls on the next
 * day in London, so a London viewer's invoice dropped it and counted the trust twice.
 *
 * These helpers use the firm's zone (Firm.timeZone) instead. When the firm has none they use the
 * browser's zone, so a firm that has not chosen one behaves exactly as before.
 *
 * A calendar day is passed and returned as a Date at local midnight (or any time on that local
 * day), the form DateUtil.dateOnly gives date-only fields. Only its local year, month and day are
 * read.
 *
 * Known limit: in the few zones whose clocks fall back at midnight (America/Santiago,
 * America/Havana, Asia/Beirut), the repeated hour before midnight is shown on the old day by
 * firmCalendarDay but falls after that day's firmEndOfDay, which takes the first midnight. US and
 * UK zones change at 1 or 2 am and are not affected.
 */
import {endOfDay, startOfDay} from 'date-fns';
import {courtWallTimeToInstant, isValidTimeZone} from './courtTime';
import {getCurrentFirm} from './picos/currentLogin';

// The trust and invoice calculations call these once per transaction, so the zone check and the
// Intl formatter are built once per zone, not on every call.
const validZones = new Map<string, boolean>();
const dayFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * The current firm's zone, or null when it has none. A zone this browser does not know (old
 * time zone data) also gives null, so the viewer's own day is used, as before OWA-1463.
 */
export function firmTimeZone(): string | null {
    const zone = getCurrentFirm()?.timeZone;
    if (!zone) return null;
    let valid = validZones.get(zone);
    if (valid === undefined) {
        valid = isValidTimeZone(zone);
        validZones.set(zone, valid);
    }
    return valid ? zone : null;
}

function dayFormat(zone: string): Intl.DateTimeFormat {
    let format = dayFormats.get(zone);
    if (!format) {
        format = new Intl.DateTimeFormat('en-US', {timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric'});
        dayFormats.set(zone, format);
    }
    return format;
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

/** The local calendar day of `day` as yyyy-MM-dd. */
function localDate(day: Date): string {
    return `${pad(day.getFullYear(), 4)}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/** The calendar day after a yyyy-MM-dd day. */
function nextDate(date: string): string {
    const next = new Date(`${date}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    return next.toISOString().slice(0, 10);
}

/**
 * The first instant of a calendar day on the firm's clock.
 * @param day calendar day (its local year, month and day)
 * @param zone the firm's zone; the browser's when null
 */
export function firmStartOfDay(day: Date, zone: string | null = firmTimeZone()): Date {
    if (!zone) return startOfDay(day);
    return courtWallTimeToInstant(localDate(day), '00:00', zone);
}

/**
 * The last instant (to the millisecond) of a calendar day on the firm's clock.
 * @param day calendar day (its local year, month and day)
 * @param zone the firm's zone; the browser's when null
 */
export function firmEndOfDay(day: Date, zone: string | null = firmTimeZone()): Date {
    if (!zone) return endOfDay(day);
    return new Date(courtWallTimeToInstant(nextDate(localDate(day)), '00:00', zone).getTime() - 1);
}

/**
 * The firm's calendar day of an instant, as a local-midnight Date, so it compares with calendar
 * days and formats with DateUtil.formatDate.
 * @param instant a moment in time, such as a transaction date
 * @param zone the firm's zone; the browser's when null
 */
export function firmCalendarDay(instant: Date, zone: string | null = firmTimeZone()): Date {
    if (!zone) return startOfDay(instant);
    const parts = dayFormat(zone).formatToParts(instant);
    const n = (type: string) => Number(parts.find(p => p.type === type)!.value);
    return new Date(n('year'), n('month') - 1, n('day'));
}

/**
 * True when an instant lies in a period given by its first and last instants, as firmStartOfDay
 * and firmEndOfDay produce them. Both ends are inclusive; a missing end is no bound.
 */
export function isInPeriod(instant: Date, start?: Date | null, end?: Date | null): boolean {
    return (!start || instant >= start) && (!end || instant <= end);
}

/**
 * True when an instant falls on or before a calendar day, on the firm's clock. The firm-day
 * counterpart of DateUtil.isOnOrBeforeDate; false when either is missing, as that is.
 */
export function isOnOrBeforeFirmDay(instant: Date | null | undefined, day: Date | null | undefined,
                                    zone: string | null = firmTimeZone()): boolean {
    if (!instant || !day) return false;
    return firmCalendarDay(instant, zone).getTime() <= startOfDay(day).getTime();
}

/**
 * True when an instant falls on or after a calendar day, on the firm's clock. The firm-day
 * counterpart of DateUtil.isOnOrAfterDate; false when either is missing, as that is.
 */
export function isOnOrAfterFirmDay(instant: Date | null | undefined, day: Date | null | undefined,
                                   zone: string | null = firmTimeZone()): boolean {
    if (!instant || !day) return false;
    return firmCalendarDay(instant, zone).getTime() >= startOfDay(day).getTime();
}

/** A calendar day as yyyy-MM-dd, from the local year, month and day of a local-midnight Date. */
export function calendarDateString(day: Date): string {
    return localDate(day);
}

/**
 * OWA-1663: the calendar date a deadline counts from, as yyyy-MM-dd.
 *
 * A timed instant is on the firm's calendar day (7 PM Central is still that day, though it is
 * already the next day in UTC). An all-day value is a local-midnight calendar day and keeps its
 * own local year, month and day: converting it to UTC moved it a day early east of UTC.
 * @param trigger the Event time, stage entry time or other trigger
 * @param allDay true for a calendar day held as local midnight (an all-day Event, a due date)
 * @param zone the firm's zone; the browser's when null
 */
export function triggerDateString(trigger: Date, allDay: boolean, zone: string | null = firmTimeZone()): string {
    return localDate(allDay ? trigger : firmCalendarDay(trigger, zone));
}

/**
 * OWA-1666: true when a due day is before today on the firm's clock. A Deadline due today is not
 * overdue until the day has passed, whatever time of day the due date is stored at.
 * @param due the due date (its local year, month and day are the due day)
 * @param now the current moment
 * @param zone the firm's zone; the browser's when null
 */
export function isBeforeFirmToday(due: Date, now: Date = new Date(), zone: string | null = firmTimeZone()): boolean {
    return startOfDay(due).getTime() < firmCalendarDay(now, zone).getTime();
}
